import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, LayoutDashboard } from 'lucide-react';
import {
  headingVariants,
  textVariants,
  buttonVariants,
  staggerContainer,
  staggerItem,
  gpuTransform,
} from '../../landing/MotionVariants';
import { Stagger, StaggerItem } from './LandingMotion';
import GradientCanvas from './GradientCanvas';
import HeroCapabilityVisual from './hero/HeroCapabilityVisual';
import HeroMetrics from './hero/HeroMetrics';
import { useAuth } from '../../../auth/AuthProviderUnified';

// Trade disciplines VH serves — an honest "who it's for" bar (not a customer-logo claim).
const TRADES = ['Electrical', 'Mechanical', 'Plumbing', 'Concrete', 'General Contractors'];

export const HeroSection = () => {
  const { user } = useAuth();
  const reduce = useReducedMotion();
  const sectionRef = useRef(null);

  // Scroll parallax: the hero copy lifts + fades as the section exits.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });
  const textY = useTransform(scrollYProgress, [0, 1], [0, reduce ? 0 : -40]);
  const textOpacity = useTransform(scrollYProgress, [0, 0.6], [1, reduce ? 1 : 0.25]);

  return (
    <section ref={sectionRef} className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-white">
      {/* Same background as the sign-in / sign-up pages: animated mesh gradient + light scrims */}
      <div className="absolute inset-0 z-0"><GradientCanvas /></div>
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-white/40 via-white/75 to-white pointer-events-none" />
      <div className="absolute inset-0 z-[1] bg-white/55 pointer-events-none" />

      <motion.div style={{ y: textY, opacity: textOpacity }} className="relative z-10 w-full max-w-[1240px] mx-auto px-6 pt-28 pb-12">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-10 items-center">
          {/* Left: copy */}
          <motion.div variants={staggerContainer} initial="hidden" animate="visible" className="lg:col-span-6 max-w-xl">
            <motion.div
              variants={staggerItem}
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-black/10 bg-black/[0.04] backdrop-blur text-xs font-mono uppercase tracking-widest text-[#E25C00] mb-6"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400" /> Jobsite intelligence
            </motion.div>

            <motion.h1
              variants={headingVariants}
              style={gpuTransform}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1C1C1E] tracking-tighter leading-[1.0] mb-6"
            >
              The intelligence layer<br />
              <motion.span
                className="text-transparent bg-clip-text"
                style={{
                  backgroundImage: 'linear-gradient(to right, #FF8534, #FF6A00, #FF8534)',
                  backgroundSize: '200% auto',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
                animate={reduce ? undefined : { backgroundPosition: ['0% 50%', '200% 50%'] }}
                transition={reduce ? undefined : { duration: 6, ease: 'linear', repeat: Infinity }}
              >
                across every jobsite.
              </motion.span>
            </motion.h1>

            <motion.p
              variants={textVariants}
              style={gpuTransform}
              className="text-lg sm:text-xl text-slate-600 max-w-xl font-light leading-relaxed mb-9"
            >
              Virtual Hardhat turns daily field activity into live safety intelligence — analyzing tasks,
              hazards, crews, compliance, and risk before incidents happen.
            </motion.p>

            <motion.div variants={staggerItem} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap" style={gpuTransform} className="w-full sm:w-auto">
                {user ? (
                  <Link
                    to="/command-center"
                    className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full font-bold text-lg flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(255,106,0,0.4)] hover:shadow-[0_0_45px_rgba(255,106,0,0.6)] transition-shadow min-h-[52px]"
                  >
                    Go to Dashboard
                    <LayoutDashboard size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <Link
                    to="/signup"
                    className="group w-full sm:w-auto px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-full font-bold text-lg flex items-center justify-center gap-2 shadow-[0_0_30px_rgba(255,106,0,0.4)] hover:shadow-[0_0_45px_rgba(255,106,0,0.6)] transition-shadow min-h-[52px]"
                  >
                    Request Demo
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </Link>
                )}
              </motion.div>
              <motion.div variants={buttonVariants} whileHover="hover" whileTap="tap" style={gpuTransform} className="w-full sm:w-auto">
                <Link
                  to="/solutions"
                  className="w-full sm:w-auto px-8 py-4 bg-white text-[#1C1C1E] border border-black/10 shadow-sm rounded-full font-medium text-lg flex items-center justify-center gap-2 hover:bg-black/[0.03] transition-colors min-h-[52px]"
                >
                  See Bolt in action
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>

          {/* Right: interactive capability animation + icon rail */}
          <div className="hidden lg:block lg:col-span-6">
            <HeroCapabilityVisual />
          </div>
        </div>

        {/* Metric band — clearly labeled illustrative/demo */}
        <motion.div
          variants={staggerItem}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.55 }}
          className="mt-16 pt-10 border-t border-black/10"
        >
          <HeroMetrics />
        </motion.div>

        {/* "Built for the trades" bar — honest who-it's-for strip */}
        <motion.div
          variants={staggerItem}
          initial="hidden"
          animate="visible"
          transition={{ delay: 0.65 }}
          className="mt-12 pt-8 border-t border-black/10"
        >
          <p className="text-xs uppercase tracking-widest text-slate-500 mb-5">Built for the trades that build everything</p>
          <Stagger stagger={0.07} delayChildren={0.1} className="flex flex-wrap items-center gap-x-10 gap-y-4">
            {TRADES.map((t) => (
              <StaggerItem key={t} y={12}>
                <span className="text-slate-700 font-semibold text-base sm:text-lg tracking-tight opacity-80">
                  {t}
                </span>
              </StaggerItem>
            ))}
          </Stagger>
        </motion.div>
      </motion.div>
    </section>
  );
};
